import React, { useState, useEffect, useCallback } from 'react';
import { contactInquiryAPI } from '../../services/api';

const formatDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

// ── Detail modal ─────────────────────────────────────────────────────────────

const DetailModal = ({ inquiry, onClose, onDelete }) => {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm('Delete this inquiry?')) return;
    setDeleting(true);
    try {
      await contactInquiryAPI.delete(inquiry.id);
      onDelete(inquiry.id);
    } catch {
      alert('Failed to delete inquiry.');
      setDeleting(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{inquiry.name}</h3>
            <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 13 }}>{formatDate(inquiry.created_at)}</p>
          </div>
          <button onClick={onClose} style={closeBtnStyle}>✕</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Row label="Email">
            <a href={`mailto:${inquiry.email}`} style={{ color: '#630137' }}>{inquiry.email}</a>
          </Row>
          {inquiry.phone && (
            <Row label="Phone">
              <a href={`tel:${inquiry.phone}`} style={{ color: '#630137' }}>{inquiry.phone}</a>
            </Row>
          )}
          {inquiry.service && <Row label="Service">{inquiry.service}</Row>}
          <Row label="Message">
            <span style={{ whiteSpace: 'pre-wrap' }}>{inquiry.message}</span>
          </Row>
        </div>

        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button
            onClick={handleDelete}
            disabled={deleting}
            style={{ padding: '0.5rem 1rem', background: deleting ? '#aaa' : '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, cursor: deleting ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 14 }}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
          <button onClick={onClose} style={{ padding: '0.5rem 1rem', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: 8, alignItems: 'flex-start' }}>
    <span style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', paddingTop: 2 }}>{label}</span>
    <span style={{ fontSize: 14, color: '#111827' }}>{children}</span>
  </div>
);

// ── Main list page ────────────────────────────────────────────────────────────

const ContactInquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null); // inquiry shown in modal

  const load = useCallback(async () => {
    try {
      const res = await contactInquiryAPI.getAll();
      if (res.success) setInquiries(res.data);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (inquiry) => {
    // optimistically mark as read in local state
    if (!inquiry.is_read) {
      setInquiries((prev) =>
        prev.map((i) => i.id === inquiry.id ? { ...i, is_read: true } : i)
      );
    }
    // fetch full record (also marks as read on server)
    try {
      const res = await contactInquiryAPI.getById(inquiry.id);
      if (res.success) setSelected(res.data);
    } catch {
      setSelected(inquiry);
    }
  };

  const handleDelete = (id) => {
    setInquiries((prev) => prev.filter((i) => i.id !== id));
    setSelected(null);
  };

  const unread = inquiries.filter((i) => !i.is_read).length;

  if (loading) return <div style={{ padding: '2rem' }}>Loading...</div>;

  return (
    <div style={{ padding: '2rem', maxWidth: 900, margin: '0 auto' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0 }}>
          Contact Inquiries
          {unread > 0 && (
            <span style={{
              marginLeft: 10, fontSize: 13, fontWeight: 600,
              background: '#630137', color: '#fff',
              padding: '2px 10px', borderRadius: 999,
            }}>
              {unread} new
            </span>
          )}
        </h2>
        <p style={{ color: '#6b7280', fontSize: 14, marginTop: 4 }}>
          Messages submitted via the website's Contact page.
        </p>
      </div>

      {inquiries.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#9ca3af', background: '#f9fafb', borderRadius: 12, border: '1px solid #e5e7eb' }}>
          No inquiries yet.
        </div>
      ) : (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Service</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {inquiries.map((inq) => (
                <tr
                  key={inq.id}
                  onClick={() => openDetail(inq)}
                  style={{
                    borderBottom: '1px solid #f3f4f6',
                    cursor: 'pointer',
                    background: inq.is_read ? '#fff' : '#fdf8fb',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f9f0f5'}
                  onMouseLeave={(e) => e.currentTarget.style.background = inq.is_read ? '#fff' : '#fdf8fb'}
                >
                  <td style={{ ...tdStyle, fontWeight: inq.is_read ? 400 : 700 }}>{inq.name}</td>
                  <td style={tdStyle}>{inq.email}</td>
                  <td style={{ ...tdStyle, color: '#6b7280' }}>{inq.service || '—'}</td>
                  <td style={{ ...tdStyle, color: '#6b7280', whiteSpace: 'nowrap' }}>{formatDate(inq.created_at)}</td>
                  <td style={tdStyle}>
                    {inq.is_read ? (
                      <span style={badgeStyle('#dcfce7', '#166534')}>Read</span>
                    ) : (
                      <span style={badgeStyle('#fdf2f8', '#630137')}>New</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <DetailModal
          inquiry={selected}
          onClose={() => setSelected(null)}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────

const thStyle = {
  padding: '10px 16px',
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const tdStyle = {
  padding: '12px 16px',
  color: '#111827',
};

const badgeStyle = (bg, color) => ({
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  background: bg,
  color,
});

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '1rem',
};

const modalStyle = {
  background: '#fff',
  borderRadius: 16,
  padding: '1.5rem',
  width: '100%',
  maxWidth: 520,
  boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
};

const closeBtnStyle = {
  background: '#f3f4f6',
  border: 'none',
  borderRadius: 8,
  width: 32,
  height: 32,
  cursor: 'pointer',
  fontSize: 14,
  color: '#6b7280',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export default ContactInquiries;
